import React, { useState, useEffect, useContext } from 'react';
import { 
  Row, Col, Card, Button, Form, Badge, InputGroup, Table, Modal
} from 'react-bootstrap';
import { 
  Search, MapPin, Phone, Package, CheckCircle, Truck, XCircle, Clock
} from 'lucide-react';
import '../../assets/css/receiverdashboard/FoodDonation.css';
import ReceiverMasterLayout from '../../components/layout/ReceiverMasterLayout';
import AppContext from '../../context/AppContext';
import axios from 'axios';

function FoodDonation() {
  const { baseUrl } = useContext(AppContext);

  const [foodDonations, setFoodDonations] = useState([]);
  const [searchQuery, setSearchQuery]     = useState('');
  const [typeFilter, setTypeFilter]       = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [displayModal, setDisplayModal]   = useState(false);
  const [selectedFood, setSelectedFood]   = useState(null);

  useEffect(() => {
    axios.get(`${baseUrl}/donor/dashboard/viewAllDonation`)
      .then((res) => {
        if (res.data.success) setFoodDonations(res.data.donation);
      })
      .catch((err) => console.log(err));
  }, []);

  const filteredFoods = foodDonations.filter(food => {
    const matchesSearch =
      (food.food_item_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (food.pickup_address || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType     = typeFilter === 'all' || food.food_category === typeFilter;
    const matchesLocation = locationFilter === 'all' || (food.city || '').includes(locationFilter);
    const matchesUrgency  = urgencyFilter === 'all' || food.available_time === urgencyFilter;
    return matchesSearch && matchesType && matchesLocation && matchesUrgency;
  });

  // FIXED: prefixed with fd- so it never collides with another dashboard's
  // .status-* classes (that collision was the cause of the overlapping badge)
  const getStatusColor = (status) => {
    const colors = {
      'pending':   'fd-status-warning',
      'picked_up': 'fd-status-info',
      'delivered': 'fd-status-success',
      'cancelled': 'fd-status-danger',
    };
    return colors[(status || 'pending').toLowerCase()] || 'fd-status-warning';
  };

  const getStatusIcon = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'delivered': return <CheckCircle size={12} />;
      case 'picked_up': return <Truck size={12} />;
      case 'cancelled': return <XCircle size={12} />;
      default:          return <Clock size={12} />;
    }
  };

  const getStatusLabel = (status) => {
    const map = {
      pending:   'Pending',
      picked_up: 'Picked Up',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
    };
    return map[(status || 'pending').toLowerCase()] || 'Pending';
  };

  const getTimeLabel = (time) => {
    const map = {
      morning:   'Morning (8AM-12PM)',
      afternoon: 'Afternoon (12PM-4PM)',
      evening:   'Evening (4PM-8PM)',
    };
    return map[(time || '').toLowerCase()] || time;
  };

  const getCategoryBadgeColor = (category) => {
    const map = {
      cooked:    '#0d9488',
      raw:       '#f59e0b',
      packaged:  '#0ea5e9',
      bakery:    '#8b5cf6',
      beverages: '#3b82f6',
    };
    return map[(category || '').toLowerCase()] || '#6c757d';
  };

  const handleClaimClick = (food) => {
    setSelectedFood(food);
    setDisplayModal(true);
  };

  const handleCloseModal = () => {
    setDisplayModal(false);
    setSelectedFood(null);
  };

  const handleConfirmClaim = () => {
    alert(`You have claimed: ${selectedFood.food_item_name}`);
    handleCloseModal();
  };

  const totalDonations = foodDonations.length;
  const availableCount = foodDonations.filter(f => (f.status || 'pending') === 'pending').length;
  const morningCount   = foodDonations.filter(f => f.available_time === 'morning').length;
  const uniqueCities   = [...new Set(foodDonations.map(f => f.city).filter(Boolean))].length;

  return (
    <ReceiverMasterLayout>
      <div className="fd-food-donations-wrapper">

        {/* ── Header Banner ── */}
        <div className="fd-food-page-banner">
          <div className="fd-banner-text-content">
            <h1 className="fd-banner-heading">Food Donations</h1>
            <p className="fd-banner-description">Find and claim fresh food donations near you</p>
          </div>
          <div className="fd-banner-icon-wrapper">
            <Package size={64} className="fd-banner-main-icon" />
          </div>
        </div>

        {/* ── Stats Cards ── */}
        <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="fd-stat-info-card fd-card-green">
              <Card.Body>
                <div className="fd-stat-symbol">🍽️</div>
                <div className="fd-stat-details">
                  <h3 className="fd-stat-number">{totalDonations}</h3>
                  <p className="fd-stat-description">Total Donations</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="fd-stat-info-card fd-card-blue">
              <Card.Body>
                <div className="fd-stat-symbol">✅</div>
                <div className="fd-stat-details">
                  <h3 className="fd-stat-number">{availableCount}</h3>
                  <p className="fd-stat-description">Pending</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="fd-stat-info-card fd-card-orange">
              <Card.Body>
                <div className="fd-stat-symbol">🌅</div>
                <div className="fd-stat-details">
                  <h3 className="fd-stat-number">{morningCount}</h3>
                  <p className="fd-stat-description">Morning Slots</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="fd-stat-info-card fd-card-purple">
              <Card.Body>
                <div className="fd-stat-symbol">📍</div>
                <div className="fd-stat-details">
                  <h3 className="fd-stat-number">{uniqueCities}</h3>
                  <p className="fd-stat-description">Cities</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ── Filters ── */}
        <Card className="fd-filters-control-card mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col lg={4} md={6} className="mb-3 mb-lg-0">
                <InputGroup>
                  <InputGroup.Text><Search size={18} /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search food or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col lg={3} md={6} className="mb-3 mb-lg-0">
                <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  <option value="all">All Food Types</option>
                  <option value="cooked">Cooked Food</option>
                  <option value="raw">Raw Ingredients</option>
                  <option value="packaged">Packaged Food</option>
                  <option value="bakery">Bakery Items</option>
                  <option value="beverages">Beverages</option>
                </Form.Select>
              </Col>
              <Col lg={2} md={6} className="mb-3 mb-lg-0">
                <Form.Select value={urgencyFilter} onChange={(e) => setUrgencyFilter(e.target.value)}>
                  <option value="all">All Time Slots</option>
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                  <option value="evening">Evening</option>
                </Form.Select>
              </Col>
              <Col lg={3} md={6}>
                <Form.Select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                  <option value="all">All Locations</option>
                  <option value="Karachi">Karachi</option>
                  <option value="Lahore">Lahore</option>
                  <option value="Islamabad">Islamabad</option>
                  <option value="Gujranwala">Gujranwala</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ── Table ── */}
        <Card className="fd-donations-listing-card">
          <Card.Body style={{ padding: 0 }}>
            <div className="fd-table-container-wrapper">
              <Table className="fd-food-listing-table" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th>Food Item</th>
                    <th>Quantity</th>
                    <th>Pickup Date</th>
                    <th>Location</th>
                    <th>Time Slot</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFoods.length > 0 ? (
                    filteredFoods.map((food, index) => (
                      <tr key={food.id} className="fd-table-data-row">

                        {/* # */}
                        <td style={{ fontWeight: 600, color: '#666' }}>{index + 1}</td>

                        {/* Food Item */}
                        <td>
                          <div className="fd-food-info-text">
                            <strong className="fd-food-title">{food.food_item_name}</strong>
                            <div className="fd-food-category mt-1">
                              <span
                                className="fd-badge"
                                style={{ backgroundColor: getCategoryBadgeColor(food.food_category) }}
                              >
                                {food.food_category}
                              </span>
                              {food.is_freshly_prepared == 1 && (
                                <span className="fd-badge" style={{ backgroundColor: '#0d9488' }}>
                                  Fresh
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Quantity */}
                        <td>
                          <strong style={{ color: '#0d9488', fontWeight: 700 }}>
                            {food.quantity} {food.unit}
                          </strong>
                          <br />
                          <small style={{ color: '#888', fontSize: '12px' }}>
                            {food.approx_people_served} people
                          </small>
                        </td>

                        {/* Pickup Date */}
                        <td style={{ color: '#444', fontWeight: 500 }}>{food.pickup_date}</td>

                        {/* Location */}
                        <td>
                          <div className="fd-location-info-wrapper">
                            <MapPin size={15} className="fd-location-pin-icon" />
                            <div>
                              <small className="fd-location-address">{food.pickup_address}</small>
                              <span className="fd-distance-indicator">{food.city}</span>
                            </div>
                          </div>
                        </td>

                        {/* Time Slot */}
                        <td style={{ fontSize: '13px', color: '#555' }}>
                          {getTimeLabel(food.available_time)}
                        </td>

                        {/* Status */}
                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <span className={`fd-status-indicator ${getStatusColor(food.status)}`}>
                            {getStatusIcon(food.status)}
                            &nbsp;{getStatusLabel(food.status)}
                          </span>
                        </td>

                        {/* Actions */}
                        <td>
                          <div className="fd-action-buttons-group">
                            <button
                              className="fd-claim-action-btn"
                              onClick={() => handleClaimClick(food)}
                            >
                              Claim Now
                            </button>
                            <button className="fd-contact-action-btn">
                              <Phone size={13} /> {food.contact_number}
                            </button>
                          </div>
                        </td>

                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '50px 20px' }}>
                        <div className="fd-empty-results-state">
                          <h4>No Food Donations Found</h4>
                          <p>Try adjusting your filters</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* ── Modal ── */}
        <Modal show={displayModal} onHide={handleCloseModal} centered size="lg">
          <Modal.Header closeButton style={{ borderBottom: '2px solid #0d9488' }}>
            <Modal.Title style={{ color: '#0d9488', fontWeight: 700 }}>
              Confirm Food Claim
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedFood && (
              <div className="fd-modal-food-details">
                <h5>{selectedFood.food_item_name}</h5>
                <p><strong>Category:</strong> <span style={{ textTransform: 'capitalize' }}>{selectedFood.food_category}</span></p>
                <p><strong>Quantity:</strong> {selectedFood.quantity} {selectedFood.unit}</p>
                <p><strong>Serves:</strong> {selectedFood.approx_people_served} people</p>
                <p><strong>Pickup Date:</strong> {selectedFood.pickup_date}</p>
                <p><strong>Time Slot:</strong> {getTimeLabel(selectedFood.available_time)}</p>
                <p><strong>Address:</strong> {selectedFood.pickup_address}</p>
                <p><strong>City:</strong> {selectedFood.city}</p>
                <p><strong>Description:</strong> {selectedFood.food_description}</p>
                <hr />
                <h6>Contact:</h6>
                <p><Phone size={16} style={{ marginRight: 6 }} />{selectedFood.contact_number}</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="fd-modal-footer">
            <Button className="fd-modal-cancel-btn" variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            <Button
              className="fd-modal-confirm-btn"
              onClick={handleConfirmClaim}
            >
              Confirm Claim
            </Button>
          </Modal.Footer>
        </Modal>

      </div>
    </ReceiverMasterLayout>
  );
}

export default FoodDonation;